import { useEffect, useRef, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useUserInteraction } from "./useUserInteraction";
import { toPageNameFromPathname, toDTOPageFromPageName, DTOPages } from "constants/pageNames";
import { getPageInteractionDefaults } from "constants/pageInteractionDefaults";
import { ROUTES } from "routes/constants";
import { useProductContext } from "context/ProductContext";
import { useAppSelector } from "store/hooks";
import { getFirst30CharsOfSessionId, getSessionIdFromSearchParams } from "utils/sessionIdUtils";

const TRACK_PAGES = [ROUTES.QUOTE, ROUTES.APPLICATION.ROOT, ROUTES.DECISION.ROOT] as const;

export function DTOPageTracker() {
    const { pathname, search } = useLocation();
    const { isGIWL, config } = useProductContext();
    const saveContId = useAppSelector((s) => s.application.saveContId);
    const lastSentPath = useRef<string | null>(null);

    const applicationMode = config.interaction.applicationMode;

    // GIWL deep-links into the app carrying the canonical session in the URL, which
    // useGIWLQuoteHydration binds into Redux asynchronously after mount. Defer the
    // interaction until Redux holds that session — otherwise it fires once, eagerly,
    // with the pre-bind (regenerated) saveContId and never corrects itself.
    const giwlExpectedSaveContId = useMemo(
        () => (isGIWL ? getFirst30CharsOfSessionId(getSessionIdFromSearchParams(new URLSearchParams(search))) : undefined),
        [isGIWL, search]
    );

    const pageName = useMemo(() => {
        const pathnameWithoutHash = pathname.split("#")[0];
        const page = toPageNameFromPathname(pathnameWithoutHash);
        const dtoPage = toDTOPageFromPageName(page);

        const defaults = getPageInteractionDefaults(applicationMode, dtoPage);
        if (!defaults) {
            console.warn(`No interaction defaults found for DTOPage: ${dtoPage}, falling back to 'welcome'`);
            return "welcome" as DTOPages;
        }

        return dtoPage;
    }, [pathname, applicationMode]);

    const { sendUserInteraction } = useUserInteraction(pageName);

    useEffect(() => {
        const match = TRACK_PAGES.find((p) => pathname.startsWith(p));
        if (!match || lastSentPath.current === pathname) return;
        // Wait for the URL-sourced GIWL session to land in Redux before firing.
        if (giwlExpectedSaveContId && saveContId !== giwlExpectedSaveContId) return;
        sendUserInteraction();
        lastSentPath.current = pathname;
    }, [pathname, pageName, sendUserInteraction, giwlExpectedSaveContId, saveContId]);

    return null;
}
